import apiClient from '@/services/apiClient'
import { trackEvent } from '@/utils/tracking'
import { detectUserCountryInfo } from '@/utils/countryDetect'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'

// Helper to safely get mixpanel distinct id
const getMixpanelId = () => {
    try {
        if (typeof window !== 'undefined' && window.mixpanel) {
            return window.mixpanel.get_distinct_id()
        }
    } catch (_) { }
    return null
}

let profileMetadataSyncInFlight = null
const syncedProfileMetadataUserIds = new Set()

const scheduleProfileMetadataSync = async (userData) => {
    if (!userData?._id || syncedProfileMetadataUserIds.has(userData._id)) {
        return
    }

    const needsCountry = !userData.countryCode || !userData.countryName
    const timezoneZone =
        typeof userData?.timezone?.zone === 'string'
            ? userData.timezone.zone
            : typeof userData?.timezone === 'string'
              ? userData.timezone
              : ''
    const needsTimezone = !timezoneZone

    if (!needsCountry && !needsTimezone) {
        return
    }

    if (profileMetadataSyncInFlight) {
        return
    }

    profileMetadataSyncInFlight = (async () => {
        try {
            const payload = {}

            if (needsCountry) {
                const detectedCountry = await detectUserCountryInfo()
                if (detectedCountry?.countryCode) {
                    payload.countryCode = detectedCountry.countryCode
                    payload.countryName = detectedCountry.countryName || null
                }
            }

            if (needsTimezone) {
                const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
                if (zone) {
                    payload.timezone = { zone }
                }
            }

            if (Object.keys(payload).length > 0) {
                await apiClient.patch('/user/update', payload)
            }
            syncedProfileMetadataUserIds.add(userData._id)
        } catch (error) {
            console.warn('Failed to sync profile metadata:', error)
        } finally {
            profileMetadataSyncInFlight = null
        }
    })()
}

export default {
    async SignupFirebase(email, uid, timezone) {
        try {
            const tokens = {}
            try {
                const studioInvite = localStorage.getItem('studioInvite')
                if (studioInvite) tokens.CreatorInvite = studioInvite
            } catch (_) { }
            // Get AdCash click ID from localStorage (captured on landing page)
            let adcashClickId = null
            try {
                adcashClickId = localStorage.getItem('adcashClickId')
            } catch (_) { }
            const responseSignUp = await apiClient.post('/user/signupuser', {
                email: email,
                uid: uid,
                timezone: {
                    zone: timezone,
                },
                detectedLanguage: navigator.language || 'en',
                // Always create base User; type-specific upgrade happens after account creation
                typeofuser: 'User',
                ...(Object.keys(tokens).length > 0
                    ? { pendingInviteTokens: tokens }
                    : {}),
                ...(adcashClickId ? { adcashClickId } : {}),
                mixpanel_distinct_id: getMixpanelId(),
            })
            return responseSignUp
        } catch (error) {
            console.log(error.response.data.err)
        }
    },

    async signupAnonymous(payload) {
        // Get AdCash click ID from localStorage (captured on landing page)
        let adcashClickId = null
        try {
            adcashClickId = localStorage.getItem('adcashClickId')
        } catch (_) { }
        const payloadWithAdcash = {
            ...payload,
            ...(adcashClickId ? { adcashClickId } : {}),
            mixpanel_distinct_id: getMixpanelId(),
        }
        return await apiClient.post('/user/signup-anonymous', payloadWithAdcash, {
            skipAuth: true,
        })
    },

    async createAnonymousClient() {
        // Authenticate anonymously with Firebase first
        try {
            const { user } = await firebase.auth().signInAnonymously()
            if (!user) throw new Error('Firebase anonymous sign-in failed')

            // If we have a user, we can now create the backend record
            // The backend expects 'uid'

            // Retrieve referral code from storage
            let referrerCode = null
            try {
                referrerCode = localStorage.getItem('loginTargetCreatorRefCode')
            } catch (_) { }

            return await this.signupAnonymous({
                uid: user.uid,
                typeofuser: 'Client',
                timezone: {
                    zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                detectedLanguage: navigator.language || 'en',
                ...(referrerCode ? { referrerCode } : {}),
            })
        } catch (error) {
            console.error('Failed to create anonymous client:', error)
            // Sign out the orphaned Firebase anonymous user to prevent
            // App.vue's onAuthStateChanged from polling for non-existent backend data
            try {
                await firebase.auth().signOut()
            } catch (_) { }
            throw error
        }
    },

    async ContinueWith(idToken, displayname, email, uid, timezone) {
        const response = await apiClient.post('/user/loginWithToken', {
            idToken: idToken,
            timezone: {
                zone: timezone,
            },
        })

        // console.log('response', response)

        if (response.data.action === 'signup') {
            try {
                const tokens = {}
                try {
                    const studioInvite = localStorage.getItem('studioInvite')
                    if (studioInvite) tokens.CreatorInvite = studioInvite
                } catch (_) { }
                // Get AdCash click ID from localStorage (captured on landing page)
                let adcashClickId = null
                try {
                    adcashClickId = localStorage.getItem('adcashClickId')
                } catch (_) { }
                // Note: displayname is intentionally NOT passed at signup
                // Users (especially creators) should set their displayname during onboarding
                // This ensures username is generated from their chosen displayname, not auto-set from OAuth
                const responseSignUp = await apiClient.post(
                    '/user/signupuser',
                    {
                        email: email,
                        uid: uid,
                        timezone: {
                            zone: timezone,
                        },
                        detectedLanguage: navigator.language || 'en',
                        // Force base User creation regardless of UI selection
                        typeofuser: 'User',
                        ...(Object.keys(tokens).length > 0
                            ? { pendingInviteTokens: tokens }
                            : {}),
                        ...(adcashClickId ? { adcashClickId } : {}),
                        mixpanel_distinct_id: getMixpanelId(),
                    }
                )
                return responseSignUp
            } catch (error) {
                console.log(error.response.data.err)
            }
        } else {
            return response
        }
    },

    async SigninFirebase(idToken, timezone) {
        // console.log('gagaga', idToken)
        return await apiClient.post('/user/loginWithToken', {
            idToken: idToken,
            timezone: {
                zone: timezone,
            },
            mixpanel_distinct_id: getMixpanelId(),
        })
    },

    async updateUser(user) {
        console.log('Updating user', user)
        // Get AdCash click ID from localStorage if not already in user object
        // This is needed for tracking when upgrading from anonymous
        let adcashClickId = user.adcashClickId
        if (!adcashClickId) {
            try {
                adcashClickId = localStorage.getItem('adcashClickId')
            } catch (_) { }
        }
        const userWithAdcash = {
            ...user,
            ...(adcashClickId ? { adcashClickId } : {}),
        }
        const response = await apiClient.patch('/user/update', userWithAdcash)
        // MixpanelService.updateUserMixpanel(response, user)

        return response
    },

    async upgradeToCreator(userData) {
        const response = await apiClient.post('/creator/user', userData)
        trackEvent('Creator Upgrade', {
            _id: userData._id,
            username: userData.username,
            displayname: userData.displayname,
        })
        return response
    },

    async upgradeToClient() {
        // No body required for client upgrade
        const response = await apiClient.post('/client/user')
        return response
    },

    async getCurrentUser() {
        const response = await apiClient.get('/user/me')
        if (response?.data) {
            // Async side effect only: do not block auth/init flow.
            scheduleProfileMetadataSync(response.data)
        }
        return response
    },

    async getUserData() {
        return await apiClient.post('/user/sync-user-data')
    },

    async isUniqueEmail(email) {
        const response = await apiClient.get(`/user/isUniqueEmail/${email}`, {
            skipAuth: true,
        })
        return response
    },

    async getUserPreview(
        username,
        language,
        allowUnverified = false,
        includeAuth = false
    ) {
        const params = {}
        if (language) {
            params.language = language
        }
        if (allowUnverified) {
            params.allowUnverified = true
        }
        const response = await apiClient.get(
            `/user/user-preview/${encodeURIComponent(username)}`,
            {
                ...(includeAuth ? {} : { skipAuth: true }),
                params,
            }
        )
        return response
    },

    async getUserById(user_id, allowUnverified = false) {
        const params = {}
        if (allowUnverified) {
            params.allowUnverified = true
        }
        const response = await apiClient.get(
            `/user/user-preview-by-id/${user_id}`,
            {
                skipAuth: true,
                params,
            }
        )
        return response
    },

    async isUniqueUsername(username) {
        try {
            const response = await apiClient.get(
                `/user/isUniqueUsername/${encodeURIComponent(username)}`,
                {
                    skipAuth: true,
                }
            )
            // Available if 200
            return { available: response.status === 200 }
        } catch (error) {
            // If backend returns 401 for taken, treat as unavailable
            if (error?.response?.status === 401) {
                return { available: false }
            }
            throw error
        }
    },

    async getCreators(search, language) {
        const searchString =
            typeof search === 'string'
                ? search
                : search && typeof search === 'object'
                    ? search.search || ''
                    : ''
        const params = { search: searchString }
        if (language) {
            params.language = language
        }
        const response = await apiClient.get('/user/creators', {
            skipAuth: true,
            params,
        })
        return response
    },

}
